import { useCallback, useContext, useEffect, useMemo } from 'react';

import { Flex, Text } from '@chakra-ui/react';
import { toast } from 'react-toastify';

import {
  GetBootedDealsQuery,
  GetBootedDealsQueryVariables,
  useGetBootedDealsLazyQuery,
  useGetBootedDealsQuery,
} from '../../gql/generated/graphql';

import ExportDealsToXlsxButton from '../ExportDealsToXlsxButton/ExportDealsToXlsxButton';
import LETable from '../shared/Table/LETable';

import { getUnseenMessageNotificationsCount } from '../../constants/notifications';
import usePaginationControls from '../../hooks/usePaginationControls';
import { useUser } from '../../hooks/useUser';
import { FiltersContext } from '../../libs/contextLib';
import {
  DealWithCalculatedProps,
  betterBootedDealColumns,
} from '../../pages/BootedDealsPage/constants';
import { getBootedDealState } from '../../pages/BootedDealsPage/utils';

type BootedDealsTableProps = {
  startDate: Date;
  endDate: Date;
  showOnlyUnreadMessages: boolean;
};

type BootedDeal = NonNullable<
  NonNullable<GetBootedDealsQuery['getBootedDeals']>['results']
>[number];

const BootedDealsTable = ({
  startDate,
  endDate,
  showOnlyUnreadMessages,
}: BootedDealsTableProps) => {
  const user = useUser();
  const {
    filters: {
      global: {
        selectedPodIds,
        sources,
        types,
        showAssignedToMe,
        showAssignedToDeletedUsers,
        showActiveTags,
        showMatureFollowUps,
      },
    },
  } = useContext(FiltersContext);

  const paginationControls = usePaginationControls();
  const { setTotalRecords, currentPage, itemsPerPage } = paginationControls;

  const baseQueryVariables: Omit<
    GetBootedDealsQueryVariables,
    'currentPage' | 'itemsPerPage' | 'sortColumn' | 'sortDirection'
  > = useMemo(
    () => ({
      assignedToMeFilter: showAssignedToMe,
      assignedToDeletedUsersFilter: showAssignedToDeletedUsers,
      startDate,
      endDate,
      sources,
      types,
      podIds: selectedPodIds,
      unreadMessagesFilter: showOnlyUnreadMessages,
      showActiveTags,
      showMatureFollowUps,
    }),
    [
      showAssignedToMe,
      showAssignedToDeletedUsers,
      startDate,
      endDate,
      sources,
      types,
      selectedPodIds,
      showOnlyUnreadMessages,
      showActiveTags,
      showMatureFollowUps,
    ],
  );

  const mapDealsWithCalculatedProps = useCallback(
    (deals: BootedDeal[]): DealWithCalculatedProps[] =>
      deals
        .filter((deal): deal is NonNullable<BootedDeal> => deal != null)
        .map((deal) => ({
          ...deal,
          unseenNotificationsCount: getUnseenMessageNotificationsCount(user.id, deal),
          lastDealState: getBootedDealState(deal),
        })),
    [user.id],
  );

  const { data, loading, error } = useGetBootedDealsQuery({
    skip: !sources.length,
    fetchPolicy: 'cache-and-network',
    variables: {
      ...baseQueryVariables,
      currentPage,
      itemsPerPage,
    },
  });

  useEffect(() => {
    setTotalRecords(data?.getBootedDeals?.totalRecords ?? 0);
  }, [data?.getBootedDeals?.totalRecords, setTotalRecords]);

  useEffect(() => {
    if (error) {
      toast.error(`Couldn't load Booted Deals. Please refresh. Error: ${error.message}`);
    }
  }, [error]);

  const [fetchAllBootedDeals] = useGetBootedDealsLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const fetchAllDeals = useCallback(async (): Promise<DealWithCalculatedProps[]> => {
    const result = await fetchAllBootedDeals({ variables: baseQueryVariables });
    return mapDealsWithCalculatedProps(result.data?.getBootedDeals?.results ?? []);
  }, [fetchAllBootedDeals, baseQueryVariables, mapDealsWithCalculatedProps]);

  const processedDeals = useMemo<DealWithCalculatedProps[]>(
    () => mapDealsWithCalculatedProps(data?.getBootedDeals?.results ?? []),
    [data?.getBootedDeals?.results, mapDealsWithCalculatedProps],
  );

  return (
    <LETable
      columns={betterBootedDealColumns}
      isLoading={loading}
      items={processedDeals}
      paginationControls={paginationControls}
      redirectToDeal
      rowKeyPrefix="booted-deal"
      rowKeyProp="id"
    >
      <Flex width="100%" justifyContent="space-between">
        <Text fontSize="20px" textColor="white">
          Booted Deals
        </Text>
        <ExportDealsToXlsxButton
          deals={processedDeals}
          columns={betterBootedDealColumns}
          dealType="booted"
          onFetchAllDeals={fetchAllDeals}
        />
      </Flex>
    </LETable>
  );
};

export default BootedDealsTable;
